import type {
  AncillariesForReservation,
  AncillarySelection,
  AncillaryElectronicMiscellaneousDocumentType,
} from 'api/flights'

// Canonical EMD-A value for checked bags sold with the ticket. Validation ignores it
// (schema-enum only) and svc-flights-sabre persists it verbatim — see the `/select`
// `ancillaries` contract (baggage.ts, point (a)).
const BAGGAGE_EMD_TYPE: AncillaryElectronicMiscellaneousDocumentType = 'FLIGHT_COUPON_ASSOCIATED'
// Literal ancillary group code for baggage, per the `/select` contract (point (a)).
const BAGGAGE_ANCILLARY_GROUP_CODE = 'BG' as const

/**
 * Resolves the availability flight for a segment by `flightId`. The availability `offer.flightId`
 * is the canonical per-flight id from the flightSearch response (`flight.id`) — the SAME id echoed
 * by the seats response and now carried on the client segment (`segment.flightId`, via
 * `journeyV2Map` → `FlightV2.id`). Matching by id is exact and route-collision free.
 *
 * Availability returns one flight per LEG. For a connecting leg (multiple segments) that single
 * flight's id equals one of the leg's segment ids (its first, through-checked segment), so passing
 * any of the leg's segment `flightId`s resolves it. Returns undefined when nothing matches (e.g. a
 * non-matching segment of a connecting leg — the add-on is surfaced once per leg upstream).
 */
export function findBaggageAvailabilityFlight(
  availability: App.FlightsBaggageAvailability,
  flightId: string,
): App.FlightsBaggageAvailabilityFlight | undefined {
  return availability.flights.find(flight => flight.flightId === flightId)
}

/**
 * Builds the `AncillariesForReservation` payload sent to `POST /api/flights/select`
 * (`additionalPayload.ancillaries`) from the persisted baggage selections + the current
 * availability. Pure (no redux) so both the modal-resolve path and the refresh
 * recovery path can reuse it.
 *
 * Satisfies the `/select` baggage validation contract:
 * - `ancillaryOfferId` = availability `offerId`.
 * - `ancillariesByFlight` keyed by `offer.flightId` verbatim.
 * - each line's `basePriceAmount` = the offer BASE amount.
 * - `totalCostAmount` = Σ offer TOTAL amount × quantity; base and total differ
 *   when the fare is tax-inclusive, so the top-level total uses `amount` while each line
 *   uses `basePriceAmount`.
 *
 * Returns `undefined` when no selection resolves to a live availability option (e.g. every
 * saved option expired), so the caller can re-price without ancillaries to zero the fee.
 */
export function buildBaggageAncillaries(
  selections: Record<string, Record<string, App.FlightBaggageSelection>>,
  availability: App.FlightsBaggageAvailability,
): AncillariesForReservation | undefined {
  // flightId -> travellerId -> optionId -> option, for a stable verbatim price lookup.
  const optionLookup = new Map<string, Map<string, Map<string, App.FlightsBaggageAvailabilityOption>>>()
  for (const flight of availability.flights) {
    const travellerMap = new Map<string, Map<string, App.FlightsBaggageAvailabilityOption>>()
    for (const traveller of flight.travellers) {
      travellerMap.set(
        traveller.travellerId,
        new Map(traveller.options.map(option => [option.id, option])),
      )
    }
    optionLookup.set(flight.flightId, travellerMap)
  }

  const ancillariesByFlight: { [flightId: string]: Array<AncillarySelection> } = {}
  let totalCostAmount = 0

  // selections is nested per leg: flightId -> travellerId -> selection. A traveller may hold a
  // selection on more than one leg, so iterate both levels and group by the leg's flightId.
  for (const travellerSelections of Object.values(selections)) {
    for (const [travellerId, selection] of Object.entries(travellerSelections)) {
      const option = optionLookup.get(selection.flightId)?.get(travellerId)?.get(selection.optionId)
      if (!option) { continue }

      const line: AncillarySelection = {
        travellerId,
        selectedFareId: selection.selectedFareId,
        quantity: 1,
        ancillarySubCode: option.subCode,
        airlineCode: option.airline,
        ancillaryGroupCode: BAGGAGE_ANCILLARY_GROUP_CODE,
        electronicMiscellaneousDocumentType: BAGGAGE_EMD_TYPE,
        basePriceAmount: option.basePriceAmount,
      }

      const existing = ancillariesByFlight[selection.flightId] ?? []
      existing.push(line)
      ancillariesByFlight[selection.flightId] = existing
      totalCostAmount += option.amount * line.quantity
    }
  }

  if (!Object.keys(ancillariesByFlight).length) {
    return undefined
  }

  return {
    ancillaryOfferId: availability.offerId,
    totalCostAmount,
    currency: availability.currency,
    ancillariesByFlight,
  }
}
